<template>
    <div v-if="maintenance" class="maintenance-screen">
        <i class="fa-solid fa-screwdriver-wrench maintenance-icon"></i>
        <h1>{{ $LANG.Get('labels.maintenanceTitle') }}</h1>
        <p>{{ $LANG.Get('labels.maintenanceMessage') }}</p>
    </div>
    <div v-else-if="$route.meta.public">
        <RouterView></RouterView>
    </div>
    <div v-else-if="sessionCheckDone" class="app-container">
        <NevsLoader v-if="$store.state.loaderCount > 0"></NevsLoader>
        <NevsNotification></NevsNotification>
        <LoginForm v-if="$store.state.user === null" :postLogin="setupMenu"></LoginForm>
        <template v-if="$store.state.user !== null">
            <TermsAcceptance v-if="!termsAccepted" @accepted="handleTermsAccepted"></TermsAcceptance>
            <template v-else>
                <Transition name="main-menu">
                    <NevsMainMenu v-show="showMenu" :items="menu.items" :logo="menu.logo"
                                  @toggleMenu="showMenu=!showMenu"></NevsMainMenu>
                </Transition>
                <NevsTopBar :breadcrumbs="$store.state.breadcrumbs" :buttons="topBarButtons"
                            @toggleMenu="showMenu=!showMenu"></NevsTopBar>
                <div class="nevs-main-content">
                    <div v-if="$store.state.user.financial_status_id === 2" class="financial-warning">
                        {{ $LANG.Get('alerts.accountWarned') }}
                    </div>
                    <div v-if="$store.state.user.financial_status_id !== 2" class="financial-warning-placeholder"></div>
                    <div v-if="maintenanceDate !== null" class="maintenance-planned-warning">
                        {{ maintenancePlannedText }}
                    </div>
                    <div v-show="!$router.currentRoute.value.path.startsWith('/messages')" v-for="(message, key) in messages" :key="key" class="unread-message">
                        <NevsButton @click="openMessage(message.id)">{{ $LANG.Get('buttons.openMessage') }}</NevsButton>
                        {{ $LANG.Get('labels.newMessage') }} - {{ message.title }}
                        <span class="unread-message-time">({{ message.time_display }})</span>
                    </div>
                    <RouterView></RouterView>
                </div>
                <HelpOverlay @done="closeHelpOverlay" :show="helpOverlay.show" :items="helpOverlay.items"></HelpOverlay>
            </template>
        </template>
        <NevsPopup></NevsPopup>
    </div>
</template>

<script>

import NevsLoader from "@/components/nevs/NevsLoader.vue";
import NevsNotification from "@/components/nevs/NevsNotification.vue";
import NevsPopup from "@/components/nevs/NevsPopup.vue";
import LoginForm from "@/components/general/LoginForm.vue";
import TermsAcceptance from "@/components/general/TermsAcceptance.vue";
import NevsMainMenu from "@/components/nevs/NevsMainMenu.vue";
import NevsTopBar from "@/components/nevs/NevsTopBar.vue";
import HelpOverlay from "@/components/general/HelpOverlay.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";

export default {
    name: 'App',
    components: {
        NevsButton,
        HelpOverlay,
        NevsMainMenu,
        NevsTopBar,
        LoginForm,
        TermsAcceptance,
        NevsPopup,
        NevsNotification,
        NevsLoader
    },
    data() {
        return {
            helpOverlay: {
                show: false,
                items: []
            },
            maintenance: false,
            maintenanceDate: null,
            maintenanceTime: null,
            maintenanceHours: null,
            showMenu: true,
            sessionCheckDone: false,
            menu: {
                logo: import.meta.env.BASE_URL + 'logo.png',
                items: []
            },
            topBarButtons: [
                {
                    icon: '<i class="fa-solid fa-envelope"></i>',
                    tooltip: this.$LANG.Get('tooltips.messages'),
                    action: () => {
                        this.$router.push('/messages');
                    }
                },
                {
                    icon: '<i class="fa-solid fa-circle-question"></i>',
                    tooltip: this.$LANG.Get('tooltips.help'),
                    action: () => {
                        this.openHelpOverlay();
                    }
                },
                {
                    icon: '<i class="fa-solid fa-user"></i>',
                    tooltip: this.$LANG.Get('tooltips.myProfile'),
                    action: () => {
                        this.$router.push('/users/' + this.$store.state.user.id);
                    }
                },
                {
                    icon: '<i class="fa-solid fa-right-from-bracket"></i>',
                    tooltip: this.$LANG.Get('tooltips.logout'),
                    action: () => {
                        let vm = this;
                        this.$API.APICall('post', 'logout', {}, (data, success) => {
                            if (success) {
                                vm.$store.commit('setUser', null);
                            } else {
                                vm.$HELPERS.HandleServerError(vm, data);
                            }
                        });
                    }
                }
            ],
            messages: [],
            crossTabHandle: null
        }
    },
    computed: {
        termsAccepted() {
            // A logged-in user must have accepted the current terms version before the app is shown. Both values
            // come from the session data (accepted `terms` column vs current `latest_terms` from terms.json).
            const user = this.$store.state.user;
            if (user === null) return true;
            return String(user.terms) === String(user.latest_terms);
        },
        maintenancePlannedText() {
            if (this.maintenanceDate === null) return '';
            const dateParts = this.maintenanceDate.split('-');
            const formattedDate = `${parseInt(dateParts[2])}.${parseInt(dateParts[1])}.${dateParts[0]}.`;
            const formattedTime = this.maintenanceTime ? this.maintenanceTime.substring(0, 5) : '';
            return this.$LANG.Get('labels.maintenancePlanned')
                .replace('%date%', formattedDate)
                .replace('%time%', formattedTime)
                .replace('%hours%', this.maintenanceHours);
        }
    },
    methods: {
        handleTermsAccepted() {
            // The terms gate updates the store user itself; make sure the menu is ready for the app that follows.
            this.setupMenu();
        },
        openMessage(messageId) {
            this.$router.push('/messages/' + messageId);
        },
        closeHelpOverlay() {
            this.helpOverlay.show = false;
        },
        openHelpOverlay() {
            window.scrollTo({top: 0});
            this.helpOverlay.items = this.$store.state.helpOverlay;
            this.$nextTick(()=>{
                this.helpOverlay.show = true;
            });
        },
        resolveWindowResize() {
            this.showMenu = window.innerWidth >= 800;
        },
        setupMenu() {
            let hiddenModules = this.$store.state.user.client.hidden_modules;

            this.menu.items = [];
            this.menu.items.push({
                id: 'dashboard',
                label: this.$LANG.Get('modules.dashboard'),
                link: '/',
                icon: '<i class="fa-solid fa-tachometer-alt"></i>',
                children: []
            });
            this.menu.items.push({
                id: '---'
            });
            if (!hiddenModules.includes('persons')) this.menu.items.push({
                id: 'persons',
                label: this.$LANG.Get('modules.persons'),
                link: '/persons',
                icon: '<i class="fa-solid fa-users"></i>',
                children: []
            });
            if (!hiddenModules.includes('companies')) this.menu.items.push({
                id: 'companies',
                label: this.$LANG.Get('modules.companies'),
                link: '/companies',
                icon: '<i class="fa-solid fa-industry"></i>',
                children: []
            });
            this.menu.items.push({
                id: '---'
            });
            if (!hiddenModules.includes('products')) this.menu.items.push({
                id: 'products',
                label: this.$LANG.Get('modules.products'),
                link: '/products',
                icon: '<i class="fa-solid fa-cubes"></i>',
                children: []
            });
            if (this.$store.state.user.client.plus) {
                if (!hiddenModules.includes('wares')) this.menu.items.push({
                    id: 'wares',
                    label: this.$LANG.Get('modules.wares'),
                    link: '/wares',
                    icon: '<i class="fa-solid fa-box-open"></i>',
                    children: []
                });
                if (!hiddenModules.includes('receiptNotes')) this.menu.items.push({
                    id: 'receiptNotes',
                    label: this.$LANG.Get('modules.receiptNotes'),
                    link: '/receipt-notes',
                    icon: '<i class="fa-solid fa-truck-ramp-box"></i>',
                    children: []
                });
                if (!hiddenModules.includes('supplies')) this.menu.items.push({
                    id: 'supplies',
                    label: this.$LANG.Get('modules.supplies'),
                    link: '/supplies',
                    icon: '<i class="fa-solid fa-warehouse"></i>',
                    children: []
                });
            }
            this.menu.items.push({
                id: '---'
            });
            if (!hiddenModules.includes('workOrders')) this.menu.items.push({
                id: 'workOrders',
                label: this.$LANG.Get('modules.workOrders'),
                link: '/work-orders',
                icon: '<i class="fa-solid fa-person-digging"></i>',
                children: []
            });
            if (!hiddenModules.includes('offers')) this.menu.items.push({
                id: 'offers',
                label: this.$LANG.Get('modules.offers'),
                link: '/offers',
                icon: '<i class="fa-solid fa-file-invoice"></i>',
                children: []
            });
            if (!hiddenModules.includes('invoices')) this.menu.items.push({
                id: 'invoices',
                label: this.$LANG.Get('modules.invoices'),
                link: '/invoices',
                icon: '<i class="fa-solid fa-file-invoice-dollar"></i>',
                children: []
            });
            if (!hiddenModules.includes('dispatchNotes')) this.menu.items.push({
                id: 'dispatchNotes',
                label: this.$LANG.Get('modules.dispatchNotes'),
                link: '/dispatch-notes',
                icon: '<i class="fa-solid fa-truck-ramp-box"></i>',
                children: []
            });
            if (this.$store.state.user.client.plus) {
                if (!hiddenModules.includes('orderNotes')) this.menu.items.push({
                    id: 'orderNotes',
                    label: this.$LANG.Get('modules.orderNotes'),
                    link: '/order-notes',
                    icon: '<i class="fa-solid fa-cart-shopping"></i>',
                    children: []
                });
            }
            if (!hiddenModules.includes('payments')) this.menu.items.push({
                id: 'payments',
                label: this.$LANG.Get('modules.payments'),
                link: '/payments',
                icon: '<i class="fa-solid fa-money-bill"></i>',
                children: []
            });
            // Only the clients with a bank connection have transactions to show. Their routes are closed for
            // everybody else as well (TransactionsMiddleware).
            if (this.$store.state.user.ais) this.menu.items.push({
                id: 'transactions',
                label: this.$LANG.Get('modules.transactions'),
                link: '/transactions',
                icon: '<i class="fa-solid fa-building-columns"></i>',
                children: []
            });
            if (this.$store.state.user.client.plus) {
                this.menu.items.push({
                    id: '---'
                });
                if (!hiddenModules.includes('contracts')) this.menu.items.push({
                    id: 'contracts',
                    label: this.$LANG.Get('modules.contracts'),
                    link: '/contracts',
                    icon: '<i class="fa-solid fa-file-signature"></i>',
                    children: []
                });
                if (!hiddenModules.includes('automaticInvoices')) this.menu.items.push({
                    id: 'wares',
                    label: this.$LANG.Get('modules.automaticInvoices'),
                    link: '/automatic-invoices',
                    icon: '<i class="fa-solid fa-repeat"></i>',
                    children: []
                });
                if (!hiddenModules.includes('incomingInvoices')) this.menu.items.push({
                    id: 'incomingInvoices',
                    label: this.$LANG.Get('modules.incomingInvoices'),
                    link: '/incoming-invoices',
                    icon: '<i class="fa-solid fa-inbox"></i>',
                    children: []
                });
                if (!hiddenModules.includes('memberships')) this.menu.items.push({
                    id: 'memberships',
                    label: this.$LANG.Get('modules.memberships'),
                    link: '/memberships',
                    icon: '<i class="fa-solid fa-id-card"></i>',
                    children: []
                });
                if (!hiddenModules.includes('personsMemberships')) this.menu.items.push({
                    id: 'personsMemberships',
                    label: this.$LANG.Get('modules.personsMemberships'),
                    link: '/persons-memberships',
                    icon: '<i class="fa-solid fa-user-tag"></i>',
                    children: []
                });
                if (!hiddenModules.includes('activities')) this.menu.items.push({
                    id: 'activities',
                    label: this.$LANG.Get('modules.activities'),
                    link: '/activities',
                    icon: '<i class="fa-solid fa-hammer"></i>',
                    children: []
                });
            }
            this.menu.items.push({
                id: '---'
            });
            if (!hiddenModules.includes('dailyReport')) this.menu.items.push({
                id: 'dailyReport',
                label: this.$LANG.Get('modules.dailyReport'),
                link: '/daily-report',
                icon: '<i class="fa-solid fa-receipt"></i>',
                children: []
            });
            if (!hiddenModules.includes('forms')) this.menu.items.push({
                id: 'forms',
                label: this.$LANG.Get('modules.forms'),
                link: 'null',
                icon: '<i class="fa-solid fa-paste"></i>',
                children: [
                    {
                        id: 'kpr',
                        label: this.$LANG.Get('modules.kpr'),
                        link: '/kpr',
                        icon: '',
                        children: []
                    },
                    {
                        id: 'posd',
                        label: this.$LANG.Get('modules.posd'),
                        link: '/posd',
                        icon: '',
                        children: []
                    }
                ]
            });
            this.menu.items.push({
                id: '---'
            });
            if (this.$store.state.user.permissions.includes('ADMINISTRATOR')) {
                this.menu.items.push({
                    id: 'settings',
                    label: this.$LANG.Get('modules.settings'),
                    link: null,
                    icon: '<i class="fa-solid fa-cog"></i>',
                    children: [
                        {
                            id: 'generalData',
                            label: this.$LANG.Get('modules.generalData'),
                            link: '/general-data',
                            icon: '',
                            children: []
                        },
                        {
                            id: 'financialSettings',
                            label: this.$LANG.Get('modules.financialSettings'),
                            link: '/financial-settings',
                            icon: '',
                            children: []
                        },
                        {
                            id: 'emailSettings',
                            label: this.$LANG.Get('modules.emailSettings'),
                            link: '/email-settings',
                            icon: '',
                            children: []
                        },
                        ...(this.$store.state.user.client.plus ? [{
                            id: 'membershipSettings',
                            label: this.$LANG.Get('modules.membershipSettings'),
                            link: '/membership-settings',
                            icon: '',
                            children: []
                        }] : []),
                        {
                            id: 'modulesSettings',
                            label: this.$LANG.Get('modules.modules'),
                            link: '/modules-settings',
                            icon: '',
                            children: []
                        },
                        {
                            id: 'users',
                            label: this.$LANG.Get('modules.users'),
                            link: '/users',
                            icon: '',
                            children: []
                        }
                    ]
                });
            }
            this.menu.items.push({
                id: '---'
            });
            this.menu.items.push({
                id: 'subscription',
                label: this.$LANG.Get('modules.subscription'),
                link: '/subscription',
                icon: '<i class="fa-solid fa-repeat"></i>',
                children: []
            });
            this.menu.items.push({
                id: 'assistance',
                label: this.$LANG.Get('modules.assistance'),
                link: '/assistance',
                icon: '<i class="fa-solid fa-headset"></i>',
                children: []
            });
            this.menu.items.push({
                id: 'help',
                label: this.$LANG.Get('modules.help'),
                link: '/help',
                icon: '<i class="fa-solid fa-suitcase-medical"></i>',
                children: []
            });
            this.menu.items.push({
                id: 'termsAndConditions',
                label: this.$LANG.Get('modules.termsAndConditions'),
                external: true,
                link: this.$store.state.settings.TERMS_URL,
                icon: '<i class="fa-solid fa-file-contract"></i>',
                children: []
            });
            this.menu.items.push({
                id: 'gdpr',
                label: this.$LANG.Get('modules.gdpr'),
                external: true,
                link: 'https://pausalko.com/GDPR.pdf',
                icon: '<i class="fa-solid fa-user-secret"></i>',
                children: []
            });
        },
        checkMessages() {
            let vm = this;
            this.$API.APICall('get', 'messages/unseen', {}, (data, success) => {
                if (success) {
                    vm.messages = data.messages;
                }
            });
        }
    },
    mounted() {
        window.addEventListener('resize', this.resolveWindowResize);
        this.resolveWindowResize();
        let vm = this;
        this.$API.APICall('get', 'public/version', {}, (data, success) => {
            if (!success) {
                if (data && data.error === 'maintenance in progress') {
                    vm.maintenance = true;
                }
                return;
            }
            vm.maintenanceDate = data.maintenance_date ?? null;
            vm.maintenanceTime = data.maintenance_time ?? null;
            vm.maintenanceHours = data.maintenance_hours ?? null;
            if (data.version !== this.$HELPERS.GetCookie('pausalko_version')) {
                this.$HELPERS.SetCookie('pausalko_version', data.version);
                window.location.reload(true);
            }
        });
        this.$API.APICall('get', 'session', {}, (data, success) => {
            if (success) {
                // Before setUser, which is what puts the menu on screen: it reads its stored state as it is created.
                vm.$store.commit('setUserData', data.user_data);
                vm.$store.commit('setUser', data.user);
                vm.$store.commit('setLocale', data.user.locale);
                vm.$nextTick(() => {
                    vm.checkMessages();
                    vm.setupMenu();
                });
            } else {
                vm.$store.commit('setLocale', vm.$store.state.settings.LOCALE);
            }
            vm.$nextTick(() => {
                vm.sessionCheckDone = true;
            });
        }, false);
        setInterval(() => {
            this.$API.APICall('get', 'public/heartbeat', {}, (data, success) => {
                if (!success) {
                    if (data && data.error === 'maintenance in progress') {
                        vm.maintenance = true;
                    }
                    return;
                }
                vm.maintenance = false;
                vm.maintenanceDate = data.maintenance_date ?? null;
                vm.maintenanceTime = data.maintenance_time ?? null;
                vm.maintenanceHours = data.maintenance_hours ?? null;
                if (vm.$store.state.user !== null) {
                    if (!data.logged_in) {
                        vm.$store.commit('setUser', null);
                    }
                    if (data.version !== this.$HELPERS.GetCookie('pausalko_version')) {
                        this.$HELPERS.SetCookie('pausalko_version', data.version);
                        window.location.reload(true);
                    }
                }
            }, false);
        }, 30000);
        this.crossTabHandle = this.$CROSS_TAB_BUS.ListenToEvent('check-messages', () => {
            vm.checkMessages();
        });
    },
    unmounted() {
        if (this.crossTabHandle !== null) {
            this.$CROSS_TAB_BUS.UnbindEvent(this.crossTabHandle);
        }
    }
}

</script>

<style scoped lang="scss">
@use './scss/vars' as *;

.maintenance-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    background: #f4f6f8;
    text-align: center;
    padding: 20px;
}

.maintenance-icon {
    font-size: 80px;
    color: #999;
    margin-bottom: 30px;
}

.maintenance-screen h1 {
    font-size: 28px;
    color: #444;
    margin-bottom: 15px;
    font-weight: 600;
}

.maintenance-screen p {
    font-size: 16px;
    color: #777;
    max-width: 480px;
    line-height: 1.6;
}

@media only screen and (max-width: 1920px) and (-webkit-device-pixel-ratio: 1.25) {
    :deep(.nevs-main-menu) {
        font-size: 16px;
    }
}

.financial-warning {
    background: $banner-warning;
    padding: 10px;
    color: black;
}

.financial-warning-placeholder {
    height: 1px;
}

.maintenance-planned-warning {
    margin: 10px;
    background: $banner-warning;
    padding: 10px;
    color: black;
    border-radius: 10px;
}

.unread-message {
    margin: 10px;
    background: $banner-notice;
    padding: 10px;
    color: black;
    border-radius: 10px;
}

.unread-message-time {
    font-style: italic;
    font-size: 12px;
}

/* Every grid in the app, from here rather than from each module: the routed views render inside elements that
   carry this component's scope attribute, so :deep reaches their tables however deep they sit.
   NevsActions renders a whole NevsField, so an actions cell stacks the table's cell padding on the field's own
   padding on the padding inside the control - about twice the height of the plain text cells beside it, and a
   row takes its tallest cell. Trimmed so the actions column stops setting the row height on its own. */
:deep(.nevs-table-actions) {
    padding-top: 4px;
    padding-bottom: 4px;
}

:deep(.nevs-table-actions .nevs-field) {
    padding: 0;
}

/* Vertical only: the trigger would look cramped without its horizontal padding. */
:deep(.nevs-table-actions .nevs-field-content) {
    padding-top: 5px;
    padding-bottom: 5px;
}
</style>